import { useCallback, useMemo } from 'react';
import { BenefitDefinition } from '../@types/benefit';
import { BenefitsListPage } from '../@types/benefitsListPage';
import { EntitlementType } from '../@types/entitlement';
import { ProductType } from '../@types/product';
import { useGetBenefitsListPage } from './useGetBenefitsListPage.hook';
import { useGetCanVisitDeposit } from './useGetCanVisitDeposit.hook';
import { useGetHasEntitlement } from './useGetHasEntitlement.hook';
import { useGetHasProduct, useHasWohnenPlusLight } from './useGetHasProduct.hook';
import { useInsurancePackageStatus } from './useInsurancePackageStatus';
import { useIsKeyAndLegalAvailable } from './useIsKeyAndLegalAvailable.hook';
import { useIsTenantProtectionAvailable } from './useIsTenantProtectionAvailable';
import { useIsKaeuferPlusDekra } from './useDekraServicesAvailable.hook';
import { BENEFIT_CONFIGS, BenefitConfig } from '@/components/config/benefitConfigs';
import { InsuranceActivationStatus } from '@/@types/insurance';
import { useKaeuferPlusLegalInsurance } from '@/customHooks/useKaeuferPlusLegalInsurance';
import { useGetProductsStatus } from '@/apiHooks/productsStatus.hook';

export const useGetBenefitsList = () => {
  const benefitsListPage = useGetBenefitsListPage();
  const hasMieterPlus = useGetHasProduct(ProductType.MieterPlus);
  const hasKaeuferPlus = useGetHasProduct(ProductType.KaeuferPlus);
  const hasWohnenPlus = useGetHasProduct(ProductType.WohnenPlus);
  const canVisitDeposit = useGetCanVisitDeposit();
  const isEntitledForTenantProtection = useIsTenantProtectionAvailable();
  const isEntitledForRelocationBox = useGetHasEntitlement(EntitlementType.RELOCATION_BOX);
  const isEntitledForKeyEmergency = useGetHasEntitlement(EntitlementType.KEY_AND_LEGAL_SERVICE);
  const {
    insurancePackageStatus,
    insurancePackageType,
    isRechtsberatungEntitled,
    isMietrechtsschutzEntitled,
    isMediationEntitled,
    isSchluesseldienstEntitled,
  } = useInsurancePackageStatus();
  const { isRealEstateProofRequested, isRealEstateDamageReportRequested } = useIsKaeuferPlusDekra();
  const isWP2410 = [InsuranceActivationStatus.ACTIVATED, InsuranceActivationStatus.PENDING].includes(
    insurancePackageStatus,
  );
  const isKeyAndLegalAvailable = useIsKeyAndLegalAvailable();
  const { isKaeuferPlusLegalInsuranceAvailable } = useKaeuferPlusLegalInsurance();
  const hasWohnenPlusLight = useHasWohnenPlusLight();
  const { productsStatus } = useGetProductsStatus();
  const isAutoContactAgentEntitled = productsStatus?.autoContactAgent?.plusProduct ?? false;

  const state = useMemo(
    () => ({
      hasMieterPlus,
      hasKaeuferPlus,
      hasWohnenPlus,
      canVisitDeposit,
      isEntitledForTenantProtection,
      isEntitledForRelocationBox,
      isEntitledForKeyEmergency,
      isRechtsberatungEntitled,
      isMietrechtsschutzEntitled,
      isMediationEntitled,
      isSchluesseldienstEntitled,
      insurancePackageStatus,
      insurancePackageType,
      isWP2410,
      isKeyAndLegalAvailable,
      isKaeuferPlusLegalInsuranceAvailable,
      isRealEstateProofRequested,
      isRealEstateDamageReportRequested,
      isAutoContactAgentEntitled,
    }),
    [
      hasMieterPlus,
      hasKaeuferPlus,
      hasWohnenPlus,
      canVisitDeposit,
      isEntitledForTenantProtection,
      isEntitledForRelocationBox,
      isEntitledForKeyEmergency,
      isRechtsberatungEntitled,
      isMietrechtsschutzEntitled,
      isMediationEntitled,
      isSchluesseldienstEntitled,
      insurancePackageStatus,
      insurancePackageType,
      isWP2410,
      isKeyAndLegalAvailable,
      isKaeuferPlusLegalInsuranceAvailable,
      isRealEstateProofRequested,
      isRealEstateDamageReportRequested,
      isAutoContactAgentEntitled,
    ],
  );

  const generateBenefitsList = useCallback(
    (configs: BenefitConfig[]): BenefitDefinition[] => {
      const mapped = configs
        .filter((config) => config.shouldInclude?.(state) ?? true)
        .map((config) => ({
          ...config,
          title: typeof config.title === 'function' ? config.title(state) : config.title,
          isEntitledForService: config.isEntitledForService?.(state) ?? true,
        }));

      if (!(hasWohnenPlusLight && benefitsListPage === BenefitsListPage.WohnenPlus)) return mapped;

      const entitled = mapped.filter((b) => b.isEntitledForService);
      const locked = mapped.filter((b) => !b.isEntitledForService);
      return [...entitled, ...locked];
    },
    [state, hasWohnenPlusLight, benefitsListPage],
  );

  const benefits = {
    [BenefitsListPage.MieterPlus]: generateBenefitsList(BENEFIT_CONFIGS[BenefitsListPage.MieterPlus]),
    [BenefitsListPage.WohnenPlus]: generateBenefitsList(BENEFIT_CONFIGS[BenefitsListPage.WohnenPlus]),
    [BenefitsListPage.KauferPlus]: generateBenefitsList(BENEFIT_CONFIGS[BenefitsListPage.KauferPlus]),
  };

  return benefits[benefitsListPage];
};
